import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { collection, addDoc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';
import app from './firebase';

// Inizializza Firebase Functions
const functions = getFunctions(app, 'europe-west6');

/**
 * Crea un cliente Stripe per l'utente corrente
 * @param {Object} userData - Dati dell'utente (email, nome)
 * @returns {Promise<Object>} - Risposta dalla funzione
 */
export const createStripeCustomer = async (userData) => {
  try {
    console.log('Chiamata a createStripeCustomer con dati:', userData);
    const createCustomerFunction = httpsCallable(functions, 'createStripeCustomer');
    const result = await createCustomerFunction(userData);
    console.log('Cliente Stripe creato con successo:', result.data);
    return result.data;
  } catch (error) {
    console.error('Errore nella creazione del cliente Stripe:', error);
    throw error;
  }
};

/**
 * Crea una sessione di checkout per il pagamento
 * @param {Object} checkoutData - Dati per il checkout
 * @returns {Promise<Object>} - Risposta dalla funzione con l'URL di checkout
 */
export const createCheckoutSession = async (checkoutData) => {
  try {
    console.log('Verifica disponibilità di Firebase Functions...');
    
    if (!functions) {
      console.error('Firebase Functions non è disponibile');
      throw new Error('Firebase Functions non è disponibile');
    }

    // Verifica che l'utente sia autenticato
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      console.error('Utente non autenticato');
      throw new Error('L\'utente deve essere autenticato per procedere con il pagamento');
    }
    
    console.log('Firebase Functions disponibile, stato:', {
      available: !!functions,
      initialized: !!functions,
      userAuthenticated: !!user,
      userId: user.uid,
      userEmail: user.email
    });

    // Crea un documento nella collezione checkout_sessions utilizzando l'estensione
    const checkoutSessionRef = await addDoc(collection(db, 'users', user.uid, 'checkout_sessions'), {
      price: checkoutData.lineItems[0].price,
      success_url: checkoutData.successUrl,
      cancel_url: checkoutData.cancelUrl,
      automatic_tax: { enabled: true },
      tax_id_collection: { enabled: true },
      metadata: {
        ...checkoutData.metadata,
        userId: user.uid
      },
      mode: 'subscription',
      allow_promotion_codes: true
    });

    console.log('Documento checkout_sessions creato:', checkoutSessionRef.id);

    // Ascolta i cambiamenti del documento
    return new Promise((resolve, reject) => {
      const unsubscribe = onSnapshot(checkoutSessionRef, (snap) => {
        const { error, url } = snap.data() || {};
        
        if (error) {
          console.error('Errore nella sessione di checkout:', error);
          unsubscribe();
          reject(new Error(error.message));
        }
        
        if (url) {
          console.log('URL di checkout ricevuto:', url);
          unsubscribe();
          resolve({ url });
        }
      });

      // Timeout dopo 60 secondi
      setTimeout(() => {
        unsubscribe();
        reject(new Error('Timeout nella creazione della sessione di checkout'));
      }, 60000);
    });
  } catch (error) {
    console.error('Errore nella creazione della sessione di checkout:', error);
    console.error('Dettagli errore:', {
      message: error.message,
      code: error.code,
      details: error.details,
      stack: error.stack
    });
    throw error;
  }
};

/**
 * Reindirizza l'utente alla pagina di checkout di Stripe
 * @param {string} url - URL della pagina di checkout
 */
export const redirectToCheckout = async (url) => {
  try {
    console.log('Reindirizzamento a Stripe con URL:', url);
    window.location.assign(url);
    return { success: true };
  } catch (error) {
    console.error('Errore nel reindirizzamento al checkout:', error);
    throw error;
  }
};

export const createCustomerPortalSession = async () => {
  try {
    if (!getAuth().currentUser) {
      throw new Error('Utente non autenticato');
    }

    console.log('Creazione sessione Customer Portal...');
    
    // Chiama la funzione ufficiale di Stripe per creare il link del portal
    const createPortalLink = httpsCallable(functions, 'ext-firestore-stripe-payments-createPortalLink');
    const { data } = await createPortalLink({
      returnUrl: window.location.origin + '/settings'
    });

    console.log('URL del Customer Portal ricevuto:', data.url);
    return data.url;
  } catch (error) {
    console.error('Errore nella creazione della sessione del Customer Portal:', error);
    throw error;
  }
};

export default {
  createStripeCustomer,
  createCheckoutSession,
  redirectToCheckout,
  createCustomerPortalSession,
}; 